/* ============================================================================
   sbd-polish.js — behaviour for the presentation fixes in sbd-polish.css
   ============================================================================
   ITEM 2   shiny dot tracing the first letter of every H1
   ITEM 5   chat launcher no longer covers the buy button on phones
   ITEM 10  chat launcher becomes a small round icon button on phones

   Items 5 and 10 have to be done from JavaScript: the widget renders inside
   #vivi-widget-host's shadow root, and a stylesheet in the document cannot
   cross that boundary. The root is open, so a <style> is appended into it.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE = '(max-width: 749px)';

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 2 · Shiny dot along the strokes of the first letter
     ══════════════════════════════════════════════════════════════════════

     The dot follows the true outline of the glyph. The letter is re-rendered as
     an SVG <text> and stroked with a very short dash, which the browser lays
     along the actual glyph contours in the actual font — so no per-letter path
     data is needed and it works for any character in any typeface.

     A letter with a counter ("O", "R", "e") has more than one contour. SVG
     restarts the dash pattern on each subpath, so those letters get a dot on
     each contour travelling in parallel — which reads as intended rather than
     as a bug, so it is left alone.
  */

  var SHINE_DASH = 10;      // length of the bright segment, px
  var SHINE_SELECTOR = 'h1:not(.rd-name)';

  function fontShorthand(cs) {
    // Canvas needs a single font shorthand; build it from the computed style.
    return [
      cs.fontStyle || 'normal',
      cs.fontVariant || 'normal',
      cs.fontWeight || '400',
      cs.fontSize + '/' + (cs.lineHeight === 'normal' ? 'normal' : cs.lineHeight),
      cs.fontFamily
    ].join(' ');
  }

  var measureCtx = null;

  function firstLetterIndex(text) {
    // Skip leading whitespace and punctuation — the first *letter*, not the
    // first character, so a heading opening with a quote still works.
    for (var i = 0; i < text.length; i++) {
      if (/[\p{L}\p{N}]/u.test(text[i])) return i;
    }
    return -1;
  }

  function firstTextNode(root) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        return /\S/.test(n.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    return walker.nextNode();
  }

  /* The letter is rendered ONCE, as a single SVG <text> that carries both the
     fill (the letter you read) and the animated stroke (the dot that travels
     its contours).

     An earlier version layered a stroke-only SVG on top of the original HTML
     letter. That put the character in the DOM twice, so the H1 read as
     "YYour vision" to anything extracting text — including search engines — and
     it had to keep two independently-laid-out glyphs in alignment. One element
     removes both problems: alignment is now structural rather than computed. */

  function buildShine(h1) {
    if (h1.dataset.sbdShine) return;

    var node = firstTextNode(h1);
    if (!node) return;
    var idx = firstLetterIndex(node.nodeValue);
    if (idx < 0) return;

    var letter = node.nodeValue[idx];

    /* The SVG is an atomic inline, which introduces a line-break opportunity
       between it and the character after it. On the hero H1 that was enough for
       `text-wrap: balance` to break the heading immediately after the traced
       letter, stranding "Y" on a line of its own.

       So the wrapper takes the whole FIRST WORD — traced letter plus the rest of
       its characters — and is marked `nowrap`. A word is never broken internally
       anyway, so this removes the bad break without changing how the heading
       wraps everywhere else. */
    var rest = node.nodeValue.slice(idx + 1);
    var wordEnd = rest.search(/\s/);
    var wordTail = wordEnd === -1 ? rest : rest.slice(0, wordEnd);

    var after = node.splitText(idx);
    after.splitText(1 + wordTail.length);

    var span = document.createElement('span');
    span.className = 'sbd-shine';

    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', 'sbd-shine__svg');
    svg.setAttribute('focusable', 'false');

    var text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
    text.setAttribute('class', 'sbd-shine__trace');
    text.textContent = letter;
    svg.appendChild(text);
    span.appendChild(svg);
    if (wordTail) span.appendChild(document.createTextNode(wordTail));

    after.parentNode.replaceChild(span, after);

    h1.dataset.sbdShine = '1';
    layoutShine(span);
    return span;
  }

  function layoutShine(span) {
    var svg = span.querySelector('.sbd-shine__svg');
    var text = span.querySelector('.sbd-shine__trace');
    if (!svg || !text) return;

    var letter = text.textContent;
    // Inherit type from the heading, not from the span, so the glyph matches
    // the characters beside it exactly.
    var cs = getComputedStyle(span.parentElement || span);
    var size = parseFloat(cs.fontSize) || 16;
    var extra = (cs.letterSpacing && cs.letterSpacing !== 'normal')
      ? (parseFloat(cs.letterSpacing) || 0) : 0;

    var metrics = measure(letter, cs);
    if (!metrics || !metrics.width) return;

    /* Baseline alignment, without trusting any of my own arithmetic.

       The SVG is an inline-block with `vertical-align: baseline`, so the browser
       aligns its BOTTOM EDGE to the surrounding text's baseline. Placing the
       glyph's own baseline exactly on that bottom edge (y = height) makes the two
       baselines the same line by construction.

       The first attempt instead computed a baseline offset from canvas metrics
       and corrected with a negative margin. The advance width it produced was
       right to a twentieth of a pixel, but the vertical placement was off by ~6px
       — enough that the letter read as a different size from its neighbours.
       Descenders are drawn outside the box; `overflow: visible` keeps them. */
    var pad = size * 0.08;                       // room for the glow, not layout
    /* Exact, not ceiled: the box's net advance IS the letter's advance, so any
       rounding surplus becomes a visible seam inside the first word. */
    var w = metrics.width + extra + pad * 2;
    var h = Math.ceil(metrics.ascent + pad);

    span.style.width = '';
    span.style.height = '';
    span.style.marginBottom = '';

    svg.setAttribute('width', w);
    svg.setAttribute('height', h);
    svg.setAttribute('viewBox', '0 0 ' + w + ' ' + h);
    // Negative margins keep the glow padding from displacing adjacent glyphs.
    // letter-spacing needs no compensation here: SVG <text> inherits it from
    // the heading, so the svg's advance already carries it. Adding it AGAIN in
    // margin-right — as an earlier version did, on top of the ceil above — put
    // a double space after the traced letter, which on the uppercase Italiana
    // H1s read as a detached drop cap: "O UR MODELS", "F REQUENTLY".
    svg.style.marginLeft = -pad + 'px';
    svg.style.marginRight = -pad + 'px';

    text.setAttribute('x', pad);
    text.setAttribute('y', h);
    text.setAttribute('font-family', cs.fontFamily);
    text.setAttribute('font-size', cs.fontSize);
    text.setAttribute('font-weight', cs.fontWeight);
    text.setAttribute('font-style', cs.fontStyle);

    // Dash gap must exceed the longest contour so only one dot rides each
    // contour at a time. Six em is comfortably clear of any glyph.
    var len = Math.max(240, size * 6);
    text.style.strokeDasharray = SHINE_DASH + ' ' + len;
    text.style.setProperty('--sbd-shine-len', (len + SHINE_DASH) + 'px');
    text.style.strokeWidth = Math.max(1, size / 44) + 'px';
    span.dataset.sbdLaid = '1';
  }

  function measure(letter, cs) {
    try {
      if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
      measureCtx.font = fontShorthand(cs);
      var m = measureCtx.measureText(letter);
      var size = parseFloat(cs.fontSize) || 16;
      var asc = (typeof m.actualBoundingBoxAscent === 'number' && isFinite(m.actualBoundingBoxAscent))
        ? m.actualBoundingBoxAscent : size * 0.72;
      var desc = (typeof m.actualBoundingBoxDescent === 'number' && isFinite(m.actualBoundingBoxDescent))
        ? m.actualBoundingBoxDescent : size * 0.2;
      return { width: m.width, ascent: asc, descent: Math.max(desc, size * 0.05) };
    } catch (e) {
      return null;
    }
  }

  function relayoutAll() {
    document.querySelectorAll('.sbd-shine').forEach(layoutShine);
  }

  function initShine() {
    if (reduceMotion.matches) return;

    document.querySelectorAll(SHINE_SELECTOR).forEach(function (h1) {
      // A heading with no layout yet (inside a collapsed section, or a template
      // block that renders empty) cannot be measured. Decorating it anyway is
      // what produced a zero-width artefact in the page-title H1 on the first
      // pass, so those are watched and decorated once they actually appear.
      if (h1.getBoundingClientRect().width > 0) {
        buildShine(h1);
      } else if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (entries, obs) {
          if (entries[0].boundingClientRect.width > 0) {
            buildShine(h1);
            obs.disconnect();
          }
        });
        io.observe(h1);
      }
    });

    // Webfonts (Italiana) usually resolve after first paint, which changes both
    // the advance width and the outline the dot follows.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(relayoutAll).catch(function () {});
    }

    // fonts.ready resolves ONCE, and often before Italiana is even requested —
    // the @import that pulls it sits in a section stylesheet after the footer.
    // A font that finished loading later left the SVG sized from the fallback
    // serif's wider metrics, which read as a space after the traced letter
    // ("O UR MODELS", "F requently"). loadingdone fires for every batch of
    // font loads, so late arrivals re-lay the letters too.
    if (document.fonts && document.fonts.addEventListener) {
      document.fonts.addEventListener('loadingdone', relayoutAll);
    }

    var t;
    window.addEventListener('resize', function () {
      clearTimeout(t);
      t = setTimeout(relayoutAll, 150);
    }, { passive: true });
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEMS 5 + 10 · Chat launcher
     ══════════════════════════════════════════════════════════════════════ */

  // Speech bubble with three dots: recognizable as chat/support even with no
  // visible label. The launcher keeps an accessible name and native tooltip.
  var CHAT_ICON =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' " +
    "stroke='%231b1512' stroke-width='1.9' stroke-linecap='round' stroke-linejoin='round'%3E" +
    "%3Cpath d='M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z'/%3E" +
    "%3Ccircle cx='8.5' cy='11.5' r='1' fill='%231b1512' stroke='none'/%3E" +
    "%3Ccircle cx='12.5' cy='11.5' r='1' fill='%231b1512' stroke='none'/%3E" +
    "%3Ccircle cx='16.5' cy='11.5' r='1' fill='%231b1512' stroke='none'/%3E" +
    "%3C/svg%3E";

  var LAUNCHER_CSS = [
    /* A round icon button at the bottom-right on every viewport. 54px remains
       a generous touch target without obscuring product content. */
    '  #launcher {',
    '    position: fixed !important;',
    '    left: auto !important;',
    '    right: 18px !important;',
    '    bottom: calc(18px + var(--sbd-launcher-lift, 0px)) !important;',
    '    width: 54px !important;',
    '    height: 54px !important;',
    '    min-width: 0 !important;',
    '    padding: 0 !important;',
    '    border-radius: 50% !important;',
    '    display: grid !important;',
    '    place-items: center !important;',
    '    gap: 0 !important;',
    '    box-shadow: 0 7px 22px rgba(0,0,0,.42) !important;',
    '    transition: bottom .22s cubic-bezier(.22,1,.36,1), transform .18s ease !important;',
    '  }',
    '  #launcher:hover { transform: translateY(-2px) !important; }',
    // Suppress only the closed icon while its normal corner crosses the hero.
    // The conversation panel and inline help trigger remain available.
    '  :host([data-sbd-launcher-suppressed="1"]:not([data-sbd-chat-open="1"])) #launcher {',
    '    visibility: hidden !important;',
    '    pointer-events: none !important;',
    '  }',
    /* All visible wording is dropped; styleLauncher supplies the accessible
       label and tooltip directly on the button. */
    '  #launcher > span:not(.dot):not(#unread) { display: none !important; }',
    '  #launcher .dot {',
    '    font-size: 0 !important;',
    '    line-height: 0 !important;',
    '    width: 26px !important;',
    '    height: 26px !important;',
    '    background-image: url("' + CHAT_ICON + '") !important;',
    '    background-size: contain !important;',
    '    background-repeat: no-repeat !important;',
    '    background-position: center !important;',
    '    animation: none !important;',
    '  }',
    '  #launcher #unread {',
    '    position: absolute !important;',
    '    top: -2px !important;',
    '    right: -2px !important;',
    '  }',
    '  @media (max-width: 899px) {',
    '    #launcher { right: 14px !important; bottom: calc(14px + var(--sbd-launcher-lift, 0px)) !important; }',
    '  }',
  ].join('\n');

  function styleLauncher(host) {
    var root = host.shadowRoot;
    if (!root || root.getElementById('sbd-launcher-polish')) return false;
    var style = document.createElement('style');
    style.id = 'sbd-launcher-polish';
    style.textContent = LAUNCHER_CSS;
    root.appendChild(style);
    var launcher = root.getElementById('launcher');
    if (launcher) {
      launcher.setAttribute('aria-label', 'Chat with support');
      launcher.setAttribute('title', 'Chat with support');
    }
    return true;
  }

  /* ITEM 5 · Keep the closed launcher clear of the stacked hero.
     Measure from its fixed, unlifted position so suppression stays stable.
     Outside the hero, continue lifting the icon above docked purchase bars. */
  function liftLauncher(host) {
    var frame = 0;
    var barSelector = '.sticky-add-to-cart__bar, .sbd-sticky-bar, #stickyBar, #atelier-landing .sticky-bar';
    var chat = host.shadowRoot && host.shadowRoot.getElementById('chat');

    var visibleRect = function (element) {
      var rect = element.getBoundingClientRect();
      var style = window.getComputedStyle(element);
      if (element.closest('[hidden]') || style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return null;
      return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight ? rect : null;
    };

    var apply = function () {
      frame = 0;
      // Do not move the launcher under an open conversation. Closing the chat
      // schedules a fresh calculation against the current page position.
      if ((chat && chat.classList.contains('open')) || host.dataset.sbdChatOpen === '1') {
        host.dataset.sbdLauncherSuppressed = '0';
        return;
      }

      var lift = 0;
      var suppress = false;
      var stackedHero = window.innerWidth <= 1099 && document.querySelector('.sbd-hero-panel, .rd-purchase');
      if (stackedHero) {
        // These match LAUNCHER_CSS, independent of an in-progress CSS transition.
        var inset = window.innerWidth <= 899 ? 14 : 18;
        var size = 54;
        var gap = 12;
        var right = (document.documentElement.clientWidth || window.innerWidth) - inset;
        var bottom = window.innerHeight - inset;
        var heroRect = visibleRect(stackedHero);
        suppress = !!(heroRect && heroRect.left < right && heroRect.right > right - size &&
          heroRect.top < bottom && heroRect.bottom > bottom - size);
        var obstacles = [];
        // The inline help link remains available in the hero. Moving the icon
        // upwards through this panel would cover its delivery and pricing copy.
        if (!suppress) document.querySelectorAll(barSelector).forEach(function (element) {
          var rect = visibleRect(element);
          if (rect && rect.left < right + gap && rect.right > right - size - gap) obstacles.push(rect);
        });
        // Work upwards if more than one docked bar occupies this corner.
        obstacles.sort(function (a, b) { return b.top - a.top; }).forEach(function (rect) {
          if (bottom - lift > rect.top - gap && bottom - lift - size < rect.bottom + gap) {
            lift = Math.max(lift, bottom - rect.top + gap);
          }
        });
      } else {
        // Preserve existing docking behavior on desktop and other templates.
        document.querySelectorAll(barSelector + ', [class*="sticky-add-to-cart"]').forEach(function (bar) {
          var rect = visibleRect(bar);
          if (rect) lift = Math.max(lift, Math.max(0, window.innerHeight - rect.top) + 12);
        });
      }
      host.dataset.sbdLauncherSuppressed = suppress ? '1' : '0';
      host.style.setProperty('--sbd-launcher-lift', Math.ceil(lift) + 'px');
    };
    var schedule = function () {
      if (!frame) frame = window.requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.querySelectorAll(barSelector).forEach(function (bar) {
      new MutationObserver(schedule).observe(bar, { attributes: true, attributeFilter: ['class', 'style', 'data-stuck'] });
      bar.addEventListener('transitionend', schedule);
    });
    // Shop Pay hydration or variant changes can change the purchase area height
    // without a scroll event. Observe the stable panel, not replaced buttons.
    var hero = document.querySelector('.sbd-hero-panel, .rd-purchase');
    if (hero && window.ResizeObserver) new ResizeObserver(schedule).observe(hero);
    if (chat) new MutationObserver(schedule).observe(chat, { attributes: true, attributeFilter: ['class'] });
    new MutationObserver(schedule).observe(host, { attributes: true, attributeFilter: ['data-sbd-chat-open'] });
  }

  /* The launcher is hidden while the customizer owns the screen (see the CSS
     block "CHAT BUBBLE STANDS DOWN IN THE CUSTOMIZER"), but it must come back
     the moment the chat is actually OPEN — otherwise the "Chat with us" links
     inside the customizer would open a conversation the shopper cannot see.

     The open state lives on .chat.open INSIDE the widget's shadow root, which
     no document stylesheet can select. The root is mode:'open', so mirror that
     one bit out onto the host as an attribute the CSS can reach. */
  function reflectChatOpenState(host) {
    var root = host.shadowRoot;
    var chat = root && root.getElementById('chat');
    if (!chat) return;
    var sync = function () {
      host.dataset.sbdChatOpen = chat.classList.contains('open') ? '1' : '0';
    };
    new MutationObserver(sync).observe(chat, { attributes: true, attributeFilter: ['class'] });
    sync();
  }

  function initLauncher() {
    var tries = 0;
    var timer = setInterval(function () {
      var host = document.getElementById('vivi-widget-host');
      if (host && host.shadowRoot) {
        styleLauncher(host);
        liftLauncher(host);
        reflectChatOpenState(host);
        clearInterval(timer);
      } else if (++tries > 40) {
        // ~20s. The widget script is third-party; if it never mounts there is
        // nothing to restyle and nothing to clean up.
        clearInterval(timer);
      }
    }, 500);
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 4 · Customizer directly under the hero on /pages/design-your-companion
     ══════════════════════════════════════════════════════════════════════

     The page is two sections: `main` (the Shopify page body) and `studio` (the
     customizer). The page body is not just a hero — it is the hero followed by
     ~20KB of marketing: the four steps, the faces grid, craft, features, why-us
     and the assurances. So the customizer rendered below all of it, and someone
     arriving to design a doll had to scroll past the entire pitch to reach the
     thing they came for.

     Section order in the template JSON cannot help, because the hero and the rest
     of that content are a single section. Splitting the page body in admin would
     work but edits live content. Moving the node is the reversible option, and it
     is the pattern the theme already uses to relocate the customizer on the PDP.
  */
  function initStudioPlacement() {
    // Scoped to this one page on purpose. The homepage carries its own instance
    // of the customizer whose placement is deliberate, and a selector loose
    // enough to catch both would silently rearrange it too.
    if (!/^\/pages\/design-your-companion\/?$/.test(window.location.pathname)) return;

    var studio = document.querySelector('[id$="__studio"]');
    var hero = document.querySelector('header.hero');
    if (!studio || !hero || studio.dataset.sbdMoved) return;

    // Only act when the customizer really does sit after the hero's content;
    // if a future template already orders them correctly, leave it alone.
    if (hero.compareDocumentPosition(studio) & Node.DOCUMENT_POSITION_FOLLOWING) {
      hero.insertAdjacentElement('afterend', studio);
      studio.dataset.sbdMoved = '1';
    }
  }

  /* ══════════════════════════════════════════════════════════════════════
     ITEM 6 · The homepage's faces under "Four faces to begin with"
     ══════════════════════════════════════════════════════════════════════

     The page body's own faces grid showed the four heads as flat, single-tone
     background images. The homepage shows the same four — Luna/Suki/Penny/Kitty,
     the identical image files — as cards with a four-tone skin switcher.

     Rather than reimplement that card (markup, styles and behaviour) a second
     time, the `sbd-heads` section is added to this page's template with its
     intro block switched off, and moved into the grid's place here. One
     component, one stylesheet, one set of behaviour — reimplementing it is how
     this codebase ends up with a fix that exists on one surface and not the
     other.
  */
  function initFacesGrid() {
    if (!/^\/pages\/design-your-companion\/?$/.test(window.location.pathname)) return;

    var heads = document.querySelector('[id$="__faces"]');
    var grid = document.querySelector('.faces-grid');
    if (!heads || !grid || heads.dataset.sbdMoved) return;

    grid.insertAdjacentElement('afterend', heads);
    // Hidden rather than removed: the original markup stays in the page body, so
    // turning this off is a one-line revert and nothing is lost in admin.
    grid.setAttribute('hidden', '');
    grid.style.display = 'none';
    heads.dataset.sbdMoved = '1';
  }

  /* ══════════════════════════════════════════════════════════════════════
     IMMERSIVE STUDIO (2026-09-07, owner request)
     ══════════════════════════════════════════════════════════════════════

     On phones the customizer is a one-screen app shell sized to
     `100svh - 76px`, where the 76px is the sticky site header it has to sit
     under. Once the shopper is actually IN the customizer that header is a
     strip of navigation they are not using, so it slides away and the shell
     takes the whole viewport.

     This function only decides WHEN. The visual half — the header transform,
     the studio's pull-up and the shell's new height — is the IMMERSIVE STUDIO
     block in sbd-polish.css, all of it hanging off .sbd-studio-immersive on
     <html>. A class toggled here without that CSS would do nothing at all,
     which is this repo's most-repeated bug.

     Two entry paths, because the owner named both:

       · the PDP's "Customize her" button and the in-page step deep links —
         handled on the CAPTURE phase so the class is on BEFORE those handlers
         scroll, and their scrollIntoView lands flush against the top edge
         (the CSS zeroes scroll-padding-top in this state);
       · the shopper scrolling to it themselves — the geometry test below.

     Nothing engages before the first scroll or tap: on /pages/design-your-
     companion the studio is the first thing under the header, so a purely
     geometric test would hide the site nav the instant the page painted, which
     reads as a broken header rather than as a feature.

     There is no oscillation band. Turning the class ON pulls the studio up by
     a full header height, so the exit test immediately needs a deliberate
     scroll back up of more than that; turning it OFF drops it back down by the
     same amount, well clear of the entry test.
  */
  var IMMERSIVE_CLASS = 'sbd-studio-immersive';
  var NO_ANIM_CLASS = 'sbd-studio-immersive--instant';
  var IN_VIEW_CLASS = 'sbd-studio-in-view';
  var STUDIO_MOBILE = '(max-width: 899px)';

  function initImmersiveStudio() {
    var studio = document.getElementById('atelier-landing');
    if (!studio) return;

    var root = document.documentElement;
    var mq = window.matchMedia(STUDIO_MOBILE);
    var frame = 0;
    var holdUntil = 0;
    var settled = false;
    var chromeFlow = 0;

    /* The site header comes in two shapes and they need different treatment.
       On most pages .header-section is a 60px sticky box with the header inside
       it, taking 60px of flow at the top of the document. On the HOMEPAGE the
       header is transparent: <header-component> is position:absolute inside a
       ZERO-HEIGHT .header-section, so it floats over the hero and takes no flow
       space at all.

       That difference broke both halves of this feature on the homepage:
         · translateY(-100%) of a zero-height box is translateY(0), so the
           header did not move — the bug the owner reported;
         · and there was no flow space to reclaim, so pulling the studio up by a
           header would have dragged it off the top of the screen.

       So: measure both numbers and let the CSS use the right one for each job.
       PAINT is what you see and what the header must travel to clear the
       screen; FLOW is what it occupies in the document and therefore all the
       studio may reclaim. */
    function measureChrome() {
      var section = document.querySelector('.header-section');
      var painted = document.querySelector('#header-component') ||
                    document.querySelector('header-component') ||
                    document.querySelector('.header') || section;
      var flow = section ? section.offsetHeight : 0;
      var paint = Math.max(painted ? painted.offsetHeight : 0, flow) || 76;
      root.style.setProperty('--sbd-header-flow', flow + 'px');
      root.style.setProperty('--sbd-header-paint', paint + 'px');
      chromeFlow = flow;
      return flow;
    }

    /* The shell reserves the fixed bottom bar with padding (sbd-polish.css,
       MOBILE APP SHELL). The bar's height is not a constant — the section wraps
       it onto two rows below 560px, and the monthly-estimate text changes its
       width — so publish what it actually measures. Never 0: the bar is
       display:none while the studio is off screen, and a 0 would drop the
       reservation entirely. A ResizeObserver catches a re-wrap without waiting
       for a scroll or click to run evaluate(). */
    var stickyBar = document.getElementById('stickyBar');
    function measureStickyBar() {
      if (!stickyBar || !mq.matches) return;
      var h = Math.round(stickyBar.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--sbd-sticky-h', h + 'px');
    }
    if (stickyBar && window.ResizeObserver) new ResizeObserver(measureStickyBar).observe(stickyBar);

    function headerHeight() {
      return chromeFlow;
    }

    function evaluate() {
      frame = 0;
      measureChrome();
      measureStickyBar();

      // Is any part of the customizer on screen? Kept ahead of every early
      // return below, because the sticky price/nav bar hangs off this and it
      // has to be right even at desktop widths and mid-jump.
      //
      // That bar is position:fixed, so it stayed pinned to the bottom of the
      // window long after the customizer had scrolled away — on a product page
      // it sat directly on top of the footer's Visa/Mastercard/PayPal marks.
      // It belongs to the customizer, so it leaves with it. A zero-height box
      // is the collapsed PDP state, which is not "in view".
      var vp = studio.getBoundingClientRect();
      root.classList.toggle(
        IN_VIEW_CLASS,
        vp.height > 0 && vp.bottom > 0 && vp.top < window.innerHeight
      );

      if (!mq.matches) {
        root.classList.remove(IMMERSIVE_CLASS);
        return;
      }
      // A jump is still animating: leave the class alone until it lands, or the
      // half-finished smooth scroll would fail the geometry test and undo it.
      // The click/open handler already queued the check for when the hold ends,
      // so this just steps aside rather than queueing another one per frame.
      if (Date.now() < holdUntil) return;

      // iOS rubber-band overscroll swings every getBoundingClientRect() reading
      // past the thresholds for a few frames, so a flick at the top of the page
      // made the header pop back in and slide away again. An overscrolled frame
      // is not a scroll position the page will settle at — sit it out.
      if (window.scrollY < 0) return;

      var box = studio.getBoundingClientRect();
      // Two thresholds, not one. ENTER is deliberately tight: the top edge is
      // at (or above) where the header ends — 24px of slack for the imprecise
      // landings of scrollIntoView — and the studio still owns most of the
      // screen. KEEP is deliberately loose: once the shopper is in the studio,
      // only a decisive scroll away (a good half-screen, not a 1mm wobble while
      // picking an option) hands the header back. The pull-up alone was meant
      // to provide this gap, but URL-bar collapses and overscroll ate it.
      var on = root.classList.contains(IMMERSIVE_CLASS);
      var takeover = on
        ? box.top <= headerHeight() + 140 && box.bottom >= window.innerHeight * 0.45
        : box.top <= headerHeight() + 24 && box.bottom >= window.innerHeight * 0.6;

      // goToStep() scrolls #cfgGrid to the top of the screen on every step
      // change, and #cfgGrid sits some way below the studio's own top edge — so
      // its 10px scroll-margin left the shell hanging ~48px off the top of the
      // viewport on the PDP. Feed the real offset back as the scroll margin and
      // the customizer's own scrolling lands the shell flush instead of being
      // fought with a correcting scroll of ours.
      var anchor = document.getElementById('cfgGrid');
      if (anchor) {
        var offset = Math.round(anchor.getBoundingClientRect().top - box.top);
        if (offset >= 0 && offset < 400) root.style.setProperty('--sbd-grid-offset', offset + 'px');
      }

      // The FIRST verdict is a starting state, not a change, so it must not
      // animate: on a page whose customizer is already on screen the header
      // would otherwise visibly slide away a moment after the page painted,
      // which reads as a glitch rather than as a design. Every later flip is a
      // response to the shopper moving, and those do animate.
      if (!settled) {
        settled = true;
        if (takeover) {
          root.classList.add(NO_ANIM_CLASS);
          root.classList.add(IMMERSIVE_CLASS);
          var animate = function () { root.classList.remove(NO_ANIM_CLASS); };
          window.requestAnimationFrame(function () { window.requestAnimationFrame(animate); });
          // rAF does not run in a hidden tab, and suppressed transitions must
          // not be what the shopper finds when they switch back to it.
          setTimeout(animate, 400);
          return;
        }
      }

      root.classList.toggle(IMMERSIVE_CLASS, takeover);
    }

    function schedule(delay) {
      if (delay) { setTimeout(evaluate, delay); return; }
      // Cancel-and-re-request, NOT "skip if one is pending". A pending frame that
      // never runs — the tab was backgrounded when it was requested — would leave
      // the old guard permanently true and wedge this controller for the life of
      // the page, so every later scroll became a no-op. This cannot get stuck.
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(evaluate);
    }

    // The explicit "take me to the customizer" affordances. Capture phase: the
    // handlers that do the scrolling are a document-level capture listener in
    // blocks/buy-buttons.liquid (which scrolls in a rAF) and a document-level
    // bubble listener in snippets/sbd-step-link.liquid — this runs before both.
    document.addEventListener('click', function (event) {
      var target = event.target.closest && event.target.closest('[data-sbd-customize-her], [data-sbd-step]');
      if (!target || !mq.matches) return;
      // Straight on, without the geometry test: the studio has not been
      // scrolled to yet, and the CSS this enables is what makes the scroll that
      // follows land correctly.
      root.classList.add(IMMERSIVE_CLASS);
      holdUntil = Date.now() + 900;
      schedule(900);
    }, true);

    // Belt and braces for the PDP reveal, which announces itself.
    document.addEventListener('sbd:customizer-open', function () {
      if (!mq.matches) return;
      root.classList.add(IMMERSIVE_CLASS);
      holdUntil = Date.now() + 900;
      schedule(900);
    });

    // Re-checks, not gates. Using the customizer can change its height — a step
    // with a taller panel, the body step opting out of the one-screen shell —
    // and the verdict has to follow that, not just page scrolling.
    //
    // Bubble phase, unlike the deep-link listener above: flipping the class
    // shifts the studio up by a header, and doing that BEFORE the tapped
    // control has acted moves the layout out from under the finger mid-tap.
    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('#atelier-landing')) schedule();
    });

    new MutationObserver(function () { schedule(); }).observe(studio, {
      attributes: true,
      attributeFilter: ['data-active-step', 'class'],
    });

    /* SNAP INTO PLACE (2026-09-10, owner request). The KEEP band above stops a
       small scroll from flipping the header back in, but it leaves the studio
       sitting wherever the wobble ended — a few px of the page above peeking
       in over the shell. Once the scroll has settled (no scroll event for
       160ms, finger up), a drift inside the keep band glides back to flush, so
       the studio is either fully seated or genuinely left — never in between.
       A drift past the band is a deliberate departure; evaluate() has already
       handed the header back, the class is off, and this never runs. */
    var snapTimer = 0;
    var touching = false;
    function snapIfSettled() {
      snapTimer = 0;
      if (!mq.matches || touching) return;
      if (Date.now() < holdUntil) return;
      if (!root.classList.contains(IMMERSIVE_CLASS)) return;
      if (window.scrollY < 0) return;
      var top = studio.getBoundingClientRect().top;
      // Above flush by up to the keep band, or nudged below by a hair. The 1px
      // dead zone stops sub-pixel layout from looping smooth-scrolls forever.
      if ((top > 1 && top <= headerHeight() + 140) || (top < -1 && top >= -80)) {
        holdUntil = Date.now() + 600;
        var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollBy({ top: top, behavior: reduced ? 'auto' : 'smooth' });
        schedule(650);
      }
    }
    function queueSnap() {
      if (snapTimer) clearTimeout(snapTimer);
      snapTimer = setTimeout(snapIfSettled, 160);
    }

    window.addEventListener('scroll', function () { schedule(); queueSnap(); }, { passive: true });
    // Never snap under a resting finger: iOS keeps the scroll position still
    // while touched, the 160ms timer expires, and the page would jump mid-drag.
    document.addEventListener('touchstart', function () {
      touching = true;
      if (snapTimer) clearTimeout(snapTimer);
    }, { passive: true });
    document.addEventListener('touchend', function () { touching = false; queueSnap(); }, { passive: true });
    document.addEventListener('touchcancel', function () { touching = false; queueSnap(); }, { passive: true });
    window.addEventListener('resize', function () { schedule(); }, { passive: true });
    window.addEventListener('orientationchange', function () { schedule(); });
    if (mq.addEventListener) mq.addEventListener('change', function () { schedule(); });

    /* FULL-SCREEN STUDIO (2026-09-21, owner request): "the customizer should
       snap into place on mobile devices with a Close button in the top left".

       The scroll-based snap above only ever lined the studio up with the top
       of the page, and on iPhones the URL bar and rubber-band overscroll left
       it hanging a strip off the top. Once the shopper has OPENED the
       customizer it now stops being part of the page at all: it is pinned to
       the viewport (position:fixed, CSS block FULL-SCREEN STUDIO in
       sbd-polish.css) and the page behind it stops scrolling, until Close.

       Opened by an explicit act only — the Customize her / Open the Customizer
       buttons, the step deep links, the PDP reveal event, or a tap on the
       inline studio. Scrolling past it on the homepage never traps anyone:
       that path keeps the older scroll snap above. */
    var FOCUS_CLASS = 'sbd-studio-focus';
    var startCollapsed = studio.classList.contains('sbd-customizer--collapsed');
    if (startCollapsed) studio.classList.add('sbd-pdp-inline');
    var heldHost = null;

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'sbd-studio-close';
    closeBtn.setAttribute('aria-label', 'Close the customizer');
    closeBtn.innerHTML = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg><span>Close</span>';
    studio.appendChild(closeBtn);

    function isFocused() { return root.classList.contains(FOCUS_CLASS); }

    function enterFocus() {
      if (startCollapsed) return;
      if (!mq.matches || isFocused()) return;
      if (studio.classList.contains('sbd-customizer--collapsed')) return;
      // Hold the studio's place in the page, so nothing below it jumps up
      // while it is out of the flow and it drops back exactly where it was.
      heldHost = studio.closest('.shopify-section') || studio.parentElement;
      if (heldHost) heldHost.style.minHeight = studio.offsetHeight + 'px';
      root.classList.add(IMMERSIVE_CLASS);
      root.classList.add(IN_VIEW_CLASS);
      root.classList.add(FOCUS_CLASS);
      studio.setAttribute('role', 'dialog');
      studio.setAttribute('aria-modal', 'true');
      studio.setAttribute('aria-label', 'Customizer');
    }

    // quiet: leaving because the window grew past the phone breakpoint — just
    // put the studio back in the page, no collapsing and no scrolling.
    function exitFocus(quiet) {
      if (!isFocused()) return;
      root.classList.remove(FOCUS_CLASS);
      studio.removeAttribute('role');
      studio.removeAttribute('aria-modal');
      studio.removeAttribute('aria-label');
      if (heldHost) { heldHost.style.minHeight = ''; heldHost = null; }
      studio.scrollTop = 0;
      if (quiet) { schedule(); return; }
      holdUntil = Date.now() + 700;
      root.classList.remove(IMMERSIVE_CLASS);
      if (startCollapsed) {
        // The PDP customizer is opt-in: Close folds it away again and returns
        // the shopper to the button that opened it. Their choices are kept.
        studio.classList.add('sbd-customizer--collapsed');
        var trigger = document.querySelector('[data-sbd-customize-her]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.scrollIntoView({ block: 'center', behavior: 'auto' });
          try { trigger.focus({ preventScroll: true }); } catch (e) {}
        }
      } else {
        // Inline studio (homepage, design page): step back so it sits in the
        // lower part of the screen, still visible and one tap from reopening.
        var top = studio.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: Math.max(0, top - window.innerHeight * 0.7), behavior: 'auto' });
      }
      schedule(700);
    }

    closeBtn.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      if (startCollapsed && !isFocused()) {
        studio.classList.add('sbd-customizer--collapsed');
        var trigger = document.querySelector('[data-sbd-customize-her]');
        if (trigger) {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.scrollIntoView({ block: 'center', behavior: 'auto' });
          try { trigger.focus({ preventScroll: true }); } catch (e) {}
        }
        return;
      }
      exitFocus(false);
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isFocused() && !document.querySelector('dialog[open]')) exitFocus(false);
    });
    if (mq.addEventListener) mq.addEventListener('change', function () { if (!mq.matches) exitFocus(true); });

    // Explicit openers. setTimeout 0: the PDP reveal (blocks/buy-buttons.liquid)
    // removes the collapsed class in its own capture listener, which may run
    // after this one; the studio has to be visible before it can be pinned.
    document.addEventListener('click', function (event) {
      if (!mq.matches || !event.target.closest) return;
      if (event.target.closest('[data-sbd-customize-her], [data-sbd-step]')) {
        setTimeout(enterFocus, 0);
        return;
      }
      var link = event.target.closest('a[href]');
      if (link && link.hash === '#configurator' && link.pathname === window.location.pathname) {
        event.preventDefault();
        enterFocus();
      }
    }, true);
    document.addEventListener('sbd:customizer-open', function () { setTimeout(enterFocus, 0); });

    // A tap on the inline studio opens it. That tap only opens: letting it also
    // pick an option would act on a control that jumps under the finger.
    studio.addEventListener('click', function (event) {
      if (startCollapsed) return;
      if (!mq.matches || isFocused() || event.target.closest('.sbd-studio-close, #savedDesignNotice')) return;
      if (studio.classList.contains('sbd-customizer--collapsed')) return;
      event.preventDefault();
      event.stopPropagation();
      enterFocus();
    }, true);

    // Arriving from another page's "Open the Customizer" link (/#configurator).
    if (window.location.hash === '#configurator' && mq.matches) {
      setTimeout(enterFocus, 300);
    }

    // Decide immediately: the header is hidden because the customizer is on
    // screen, not because the shopper did something to earn it. Called direct,
    // NOT through schedule() — the opening verdict is the one moment worth a
    // synchronous answer, and a rAF here would defer it past the first paint
    // (indefinitely, in a tab opened in the background).
    evaluate();
  }

  /* ══════════════════════════════════════════════════════════════════════
     SHELF COUNTS (2026-09-07, owner request)
     ══════════════════════════════════════════════════════════════════════

     "1–6 of 35" above every option shelf that scrolls. The gold chevron already
     hinted that more exists off the right edge; it never said 30 more, so a
     shopper who swiped once and saw two new wigs had no reason to think there
     were twenty-eight behind them.

     Only on shelves that actually overflow — a two-card pair shelf saying
     "1–2 of 2" is noise — which also scopes it to phones for free: on desktop
     the same groups lay out as a full grid with nothing hidden, so the test is
     false and no badge is inserted.

     WHY THIS IS NOT IN atelier-landing-2.js, where the shelves are built: that
     file is one half of a byte-identical synced pair with 2D Customizer/
     shopify-upload-2/ that has ALREADY diverged and is flagged high by
     tools/reconcile.mjs. Adding a fourth copy of this behaviour is exactly the
     bug this repo keeps re-living, so the badge is grafted on from here.

     Two listeners for the life of the page, never per render — renderAll()
     repaints the same #questionPanel node, so a listener attached during a
     render stacks up on every step change (the dead "Hear it" button was that
     bug). A MutationObserver catches new shelves; ONE capture-phase scroll
     listener catches swipes, because scroll does not bubble but does capture.
  */
  function initShelfCounts() {
    var studio = document.getElementById('atelier-landing');
    if (!studio || !window.MutationObserver) return;

    var frame = 0;

    function rangeFor(grid, tiles) {
      var box = grid.getBoundingClientRect();
      var first = -1, last = -1;
      for (var i = 0; i < tiles.length; i++) {
        var tile = tiles[i].getBoundingClientRect();
        var shown = Math.min(tile.right, box.right) - Math.max(tile.left, box.left);
        // 60%, not "any sliver": the half-visible next column is the scroll
        // affordance, and counting it would make the badge read one card ahead
        // of what the shopper can actually see.
        if (tile.width > 0 && shown >= tile.width * 0.6) {
          if (first < 0) first = i;
          last = i;
        }
      }
      return first < 0 ? null : [first, last];
    }

    function paint() {
      frame = 0;
      var panel = document.getElementById('questionPanel');
      if (!panel) return;

      panel.querySelectorAll('.hwrap').forEach(function (wrap) {
        var grid = wrap.querySelector('.opt-grid');
        if (!grid) return;
        var tiles = grid.querySelectorAll('.opt');
        var scrolls = grid.scrollWidth > grid.clientWidth + 8;

        var badge = wrap.previousElementSibling;
        var isBadge = badge && badge.classList && badge.classList.contains('sbd-shelf-count');

        if (!scrolls || tiles.length < 2) {
          if (isBadge) badge.remove();
          return;
        }
        if (!isBadge) {
          badge = document.createElement('div');
          badge.className = 'sbd-shelf-count';
          // The radio group already announces "3 of 35" natively as the shopper
          // moves through it; a second live-updating count would just talk over
          // it, so this is decoration for the eye only.
          badge.setAttribute('aria-hidden', 'true');
          wrap.parentNode.insertBefore(badge, wrap);
        }

        // The shelves lay out column-major (grid-auto-flow: column), so DOM
        // order runs down each column in turn and a visible column is always a
        // contiguous run — which is what makes "first–last" honest rather than
        // a count of scattered cards.
        var range = rangeFor(grid, tiles);
        if (!range) return;
        var label = (range[0] === range[1])
          ? (range[0] + 1) + ' of ' + tiles.length
          : (range[0] + 1) + '–' + (range[1] + 1) + ' of ' + tiles.length;

        // Only write on a real change. Assigning the same string still replaces
        // the text node, which the observer below would see as a mutation and
        // repaint from, forever.
        if (badge.textContent !== label) badge.textContent = label;
      });
    }

    function schedule() {
      // Same cancel-and-re-request as initImmersiveStudio: a frame requested
      // while the tab is hidden must not be able to wedge this permanently.
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(paint);
    }

    // #questionPanel only, NOT the whole studio: the stage swaps its photo and
    // the price re-renders constantly, and observing those would run this on
    // every preview change for nothing.
    var panelNode = document.getElementById('questionPanel');
    if (!panelNode) return;
    new MutationObserver(schedule).observe(panelNode, { childList: true, subtree: true });
    document.addEventListener('scroll', function (event) {
      var target = event.target;
      if (target && target.classList && target.classList.contains('opt-grid')) schedule();
    }, true);
    window.addEventListener('resize', schedule, { passive: true });
    schedule();
  }

  /* ══════════════════════════════════════════════════════════════════════
     STEP PREFETCH (2026-09-07, owner request)
     ══════════════════════════════════════════════════════════════════════

     Two jobs, both about the shelf never popping in under the shopper:

       1. FINISH THE CURRENT STEP. renderAll() eagerly loads roughly the first
          eight tiles plus the ones around the selection and leaves the rest
          lazy — so on a 30-wig shelf the tiles arrived as you swiped. Once the
          step has settled, the rest are fetched too.
       2. WARM THE NEXT STEP, so Continue lands on a shelf that is already
          drawn rather than one that fills in.

     WHY NOT JUST RENDER THE NEXT STEP OFFSCREEN, which would need no table:
     because those render functions are not side-effect free. renderMakeup()
     ends by calling runMakeupGeneration(), which is a real billed image
     generation, and the eyes step's post-render hook zooms the LIVE preview
     stage. Rendering ahead would spend money and move the shopper's screen.

     So the URLs are built from the customizer's own data instead, reached
     through the same eval bridge that sections/sbd-2d-customizer.liquid
     already uses for the cart handler: its top-level bindings live in the
     global lexical environment, which a direct eval in any classic script on
     the page can see. Every lookup is guarded — if a name ever moves, this
     silently prefetches nothing rather than breaking the customizer.

     The URL is built with the customizer's OWN imgAttrs()/tileWidth()/
     tileHeight(), not a reimplementation, so the prefetched candidate is
     byte-identical to the one the renderer will ask for — including the
     1x/2x srcset, which the browser resolves per device pixel ratio exactly
     as it will on the real tile. A near-miss here would double the bytes
     instead of halving the wait.

     Only the shelves long enough to be worth it. The two-card steps hold two
     images and were never the problem. cols/aspect mirror the radioGroup()
     calls in atelier-landing-2.js — the one piece of duplication here, and it
     costs a wrong image SIZE (wasted bytes), never a broken step. */
  var PREFETCH_STEPS = {
    hair:        { name: 'hairStyle',   cols: 'cols-6',               aspect: 'opt-portrait', imageOnly: false, list: function (s, g) { var f = g('hairListFor'); return f && s ? f(s.face.key, s.skin.key) : null; } },
    eyes:        { name: 'eyes',        cols: 'cols-6',               aspect: '',             imageOnly: true,  list: function (s, g) { return g('EYE_COLORS'); } },
    fingernails: { name: 'fingernails', cols: 'cols-6',               aspect: '',             imageOnly: true,  list: function (s, g) { return g('FINGERNAIL_STYLES'); } },
    makeup:      { name: 'makeup',      cols: 'cols-3 hscroll rows2', aspect: '',             imageOnly: false, list: function (s, g) { return g('MAKEUP_STYLES'); } },
    pubicHair:   { name: 'pubicHair',   cols: 'cols-4 hscroll',       aspect: 'wide',         imageOnly: false, list: function (s, g) { return g('PUBIC_HAIR_STYLES'); } },
  };

  // Hard ceiling per step. A shelf is dozens of tiles, not hundreds, and a
  // runaway list must never turn into an unbounded download on a phone plan.
  var PREFETCH_MAX = 40;

  function initStepPrefetch() {
    var studio = document.getElementById('atelier-landing');
    if (!studio) return;

    // Direct eval: resolves against this function's scope chain, which ends at
    // the global lexical environment holding the customizer's top-level
    // const/let. Those are NOT window properties, so window.STEPS is undefined
    // and this is the only way in without editing the (already diverged)
    // customizer bundle.
    function grab(name) {
      try { return eval(name); } catch (error) { return undefined; }
    }

    // Respect the shopper's data settings. Prefetching is a nicety; on Save
    // Data or a 2g/slow-2g connection it is a tax on someone who has asked us
    // not to, and the images still load on demand exactly as before.
    function prefetchAllowed() {
      var c = navigator.connection;
      if (!c) return true;
      if (c.saveData) return false;
      return !/^(slow-)?2g$/.test(c.effectiveType || '');
    }

    var fetched = Object.create(null);
    // Detached images are collectable, and a collected <img> can have its fetch
    // cancelled before it lands in the cache. Hold each one until it resolves.
    var inFlight = [];

    function keepAlive(img) {
      inFlight.push(img);
      var done = function () {
        var at = inFlight.indexOf(img);
        if (at > -1) inFlight.splice(at, 1);
      };
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }

    /* Build the next step's shelf with the customizer's OWN radioGroup() and
       harvest the images out of it.

       This is why the tile size is not computed here. radioGroup does not
       derive it — tileBox() MEASURES the painted box by rendering a srcless
       probe of the same markup into #questionPanel, because the rules that
       size a tile live in sbd-polish.css across five height breakpoints and
       restating them would be a second copy of a fact that already has a home.
       An earlier version of this function guessed with tileWidth(), the
       desktop fallback table, and every single URL missed: 120px asked for
       where the shelf paints 84, 232 where it paints 128. Right file, wrong
       variant, zero cache hits, double the bytes.

       radioGroup is pure — it returns a string. tileBox's probe is appended and
       removed inside its own finally, and its measurement is cached per group
       per viewport, so this also warms the cache the real render is about to
       want. */
    function warmStep(plan, state, grab) {
      var radioGroup = grab('radioGroup');
      var list = plan.list(state, grab);
      if (typeof radioGroup !== 'function' || !Array.isArray(list) || !list.length) return 0;

      var withPhotos = list.filter(function (o) { return o && o.img; }).slice(0, PREFETCH_MAX);
      if (!withPhotos.length) return 0;

      var html = radioGroup(plan.name, withPhotos, null, plan.cols, plan.aspect, plan.imageOnly);
      // The markup ships loading="lazy" for the real grid. Off-document there is
      // no viewport to be far from, and the whole point here is to fetch now.
      html = html.replace(/loading="lazy"/g, 'loading="eager"');

      var holder = document.createElement('div');
      holder.innerHTML = html;

      var n = 0;
      holder.querySelectorAll('img.opt-photo').forEach(function (img) {
        var key = img.getAttribute('src');
        if (!key || fetched[key]) return;
        fetched[key] = true;
        keepAlive(img);
        n++;
      });
      return n;
    }

    // Job 1: whatever renderAll() left lazy on the step now showing.
    function finishCurrentStep() {
      var panel = document.getElementById('questionPanel');
      if (!panel) return;
      panel.querySelectorAll('img.opt-photo[loading="lazy"]').forEach(function (img) {
        img.loading = 'eager';
      });
    }

    // Job 2: the step Continue will land on, skipping any the flow hides.
    function warmNextStep() {
      var STEPS = grab('STEPS'), stepIndex = grab('stepIndex'), isVisible = grab('isVisible');
      if (!Array.isArray(STEPS) || typeof stepIndex !== 'number') return;
      for (var i = stepIndex + 1; i < STEPS.length; i++) {
        if (typeof isVisible === 'function' && !isVisible(i)) continue;
        var plan = PREFETCH_STEPS[STEPS[i].id];
        if (plan) warmStep(plan, grab('state'), grab);
        return; // one step ahead, as asked — not the whole flow
      }
    }

    function run() {
      if (!prefetchAllowed()) return;
      // Same rule as the stage warm below: a collapsed or held-off-screen
      // customizer has no next step anyone is about to see.
      if (studio.classList.contains('sbd-customizer--collapsed') || grab('custDeferImages') === true) return;
      finishCurrentStep();
      try { warmNextStep(); } catch (error) { /* never break a step change */ }
    }

    // After the step has painted and settled, so this competes with nothing the
    // shopper is actually waiting on — but never ONLY on requestIdleCallback.
    // It can be starved indefinitely on a busy page, and in some embedded web
    // views it never runs at all (this session's own preview pane is one: rAF
    // and rIC both sit dead there). A prefetch that silently never happens is
    // the worst of both worlds — all of the code, none of the speed — so a
    // plain timer backstops it and whichever fires first wins.
    var pending = false;
    function schedulePrefetch() {
      if (pending) return;
      pending = true;
      var ran = false;
      var fire = function () {
        if (ran) return;
        ran = true;
        pending = false;
        run();
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(fire, { timeout: 1200 });
      }
      setTimeout(fire, 1500);
    }

    new MutationObserver(schedulePrefetch).observe(studio, {
      attributes: true,
      attributeFilter: ['data-active-step'],
    });
    schedulePrefetch();
  }

  /* ══════════════════════════════════════════════════════════════════════
     PREVIEW SIZING (2026-09-07, owner report)
     ══════════════════════════════════════════════════════════════════════

     The live-preview portrait already ships a proper responsive ladder —
     96/150/300/600/900w candidates off the resizing CDN. What is wrong is the
     hint that picks between them:

         sizes="(min-width:900px) 300px, 100vw"

     "100vw" is only true on desktop-shaped thinking. On a 375x812 phone the
     stage paints the portrait at 210x280 CSS px, which at DPR 2 needs 420
     device pixels — but 100vw claims 375 CSS px, i.e. 750 device pixels, so
     the browser takes the 900w rung. Measured against the CDN: 900w is 75 KB,
     600w is 40 KB. Every hair, eye, skin and makeup pick paid ~47% more than
     the stage could show, and the stage is the biggest image in the customizer.

     The painted size cannot be written as a static `sizes` value because it
     follows the stage's HEIGHT, which changes per step (the panel takes what
     its question needs). So it is measured off the element and written back.

     Corrected in a MutationObserver callback — a microtask, which runs before
     the browser's image selection for that change — so this replaces the
     request rather than adding a second one. Verified: no double fetch.

     Mobile only. On desktop the 300px in the hint matches the panel exactly. */
  function initPreviewSizing() {
    var photo = document.getElementById('previewPhoto');
    if (!photo || !window.MutationObserver) return;

    var mq = window.matchMedia('(max-width: 899px)');
    var applying = false;

    function correct() {
      if (applying) return;
      if (!mq.matches) return;
      if (!photo.getAttribute('srcset')) return;   // no ladder, nothing to choose
      var box = photo.getBoundingClientRect();
      // 15% of headroom, deliberately. The stage animates its height between
      // steps, so a measurement can land mid-transition and read smaller than
      // the box ends up — and an UNDER-estimate is the bad direction: it buys a
      // few KB and pays with a visibly upscaled portrait, on the one image the
      // whole customizer is about. Over-estimating just forfeits some of the
      // saving. The margin keeps the chosen rung >= the painted size even if
      // the measurement is a step behind.
      // The eyes step pushes the stage in with transform: scale(1.35) on
      // .preview-figure.zoomed .preview-photo. getBoundingClientRect() only
      // reflects that mid-transition, and a transform never fires the
      // ResizeObserver below — so take the untransformed layout width
      // (offsetWidth ignores transforms) and price the zoom in from the class.
      // Zoomed, the 1.35 replaces the 15% headroom rather than stacking on it:
      // 194px x 1.35 = 262px = 524 device px lands on the 600 rung, while
      // 1.35 x 1.15 = 301px = 602 tipped a phone onto 900w (+13 KB a pick).
      var zoomed = !!photo.closest('.preview-figure.zoomed');
      var css = Math.round((photo.offsetWidth || box.width) * (zoomed ? 1.35 : 1.15));
      // Before the stage has laid out there is nothing to measure, and writing
      // a 0px hint would pin the browser to the smallest rung for good.
      if (css < 40) return;
      var want = css + 'px';
      if (photo.getAttribute('sizes') === want) return;
      applying = true;
      photo.setAttribute('sizes', want);
      applying = false;
    }

    new MutationObserver(correct).observe(photo, {
      attributes: true,
      attributeFilter: ['src', 'srcset', 'sizes'],
    });

    // The stage is not a fixed box: the question panel takes what its step
    // needs and the stage absorbs the rest, so the portrait paints anywhere
    // from ~200px to ~390px wide WITHOUT the image's own attributes changing.
    // Measured once on a small step and left alone, the hint would then
    // under-serve every taller step — a blurry stage is a worse bug than an
    // oversized download. Watch the box, not just the attributes.
    if (window.ResizeObserver) new ResizeObserver(correct).observe(photo);
    // The zoom is a class on the figure, not an attribute of the photo.
    var figure = photo.closest('.preview-figure');
    if (figure) new MutationObserver(correct).observe(figure, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('resize', correct, { passive: true });
    correct();
  }

  /* ══════════════════════════════════════════════════════════════════════
     STAGE WARMING (2026-09-07, owner report: "hair and eyes are still slow")
     ══════════════════════════════════════════════════════════════════════

     The slow part was never the thumbnails. Timed against the live CDN, every
     preview render is built ON DEMAND the first time that exact variant is
     asked for:

         600w  first request 1.97s   second 0.06s
         900w  first request 2.39s   second 0.09s

     So the ~2s wait after picking a hair style is the resizer building the
     file, not the download. Prefetching tiles could never fix it, and neither
     could shrinking the image — a smaller variant is just as cold.

     The fix is to ask for those renders BEFORE the shopper taps, so the build
     has already happened. Demand-driven rather than greedy: only the tiles
     actually on screen in the shelf (plus a margin), re-run as they swipe. A
     shopper who picks the second wig and moves on pays for a handful of
     renders, not all thirty — which at ~40KB each would be ~1.2MB of someone's
     phone plan spent on options they never looked at.

     The URL is built with the customizer's own faceImg()/cdnImg() and handed
     the SAME srcset ladder and sizes hint as the live stage, so the browser
     resolves the identical candidate. A near-miss would warm a variant nobody
     then asks for — all of the cost, none of the benefit, because each rung is
     built and cached separately. */
  var STAGE_WARM_STEPS = {
    // Which selection each step changes, and therefore what varies in the render.
    hair: function (o, st, g) { var f = g('faceImg'); return o.hair && st.eyes ? f(st.face.key, st.skin.key, st.eyes.eyeNum, o.hair) : null; },
    eyes: function (o, st, g) { var f = g('faceImg'); return o.eyeNum && st.hairStyle ? f(st.face.key, st.skin.key, o.eyeNum, st.hairStyle.hair) : null; },
    // Hair keys are per tone (Luna/Tanned '10' is J9, Luna/DarkTanned is 'J9'), so another
    // tone's render needs THAT tone's key for the same wig — the same selection renderSkin
    // makes. Reusing the current key 404'd every tone but the current one (JSON body, which
    // Chrome reports as ERR_BLOCKED_BY_ORB) and never warmed a single tone switch.
    skin: function (o, st, g) {
      var f = g('faceImg'), list = g('hairListFor'), keep = g('retainHairSelection');
      if (!st.eyes || !st.hairStyle || typeof list !== 'function' || typeof keep !== 'function') return null;
      var hs = keep(list(st.face.key, o.key), st.hairStyle);
      return hs && hs.hair ? f(st.face.key, o.key, st.eyes.eyeNum, hs.hair) : null;
    },
  };
  var STAGE_WARM_LISTS = {
    hair: function (st, g) { var f = g('hairListFor'); return f ? f(st.face.key, st.skin.key) : null; },
    eyes: function (st, g) { return g('EYE_COLORS'); },
    skin: function (st, g) { var f = g('skinToneCards'); return f ? f(st.face.key) : null; },
  };
  // Mirrors the ladder updatePreview() offers. Drift here costs a wasted warm,
  // never a broken stage.
  var STAGE_WARM_SELECTED = {
    hair: function (st) { return st.hairStyle; },
    eyes: function (st) { return st.eyes; },
    skin: function (st) { return st.skin; },
  };
  var STAGE_LADDER = [96, 150, 300, 450, 600, 900];
  // Either side of the next step's selection — its shelf opens centred there.
  var STAGE_LOOKAHEAD = 3;
  // Tiles either side of what is on screen. The shelves are two rows laid out
  // column-major, so this is 2 columns of lead — about one swipe — and a cold
  // render takes ~2s to build, which is roughly how long a swipe buys us.
  var STAGE_MARGIN = 4;
  var STAGE_CONCURRENCY = 3; // a cold variant is ~2s of CDN work — do not flood it

  function initStageWarm() {
    var studio = document.getElementById('atelier-landing');
    if (!studio) return;

    function grab(name) {
      try { return eval(name); } catch (error) { return undefined; }
    }
    function allowed() {
      var c = navigator.connection;
      if (!c) return true;
      return !c.saveData && !/^(slow-)?2g$/.test(c.effectiveType || '');
    }

    var warmed = Object.create(null);
    var queue = [];
    var active = 0;
    // The stage is a different size on different steps — the eyes step zooms it
    // 1.35x, for one — so the rung a step asks for is not the rung the step
    // before it asked for. Warming the wrong rung is pure waste: each rung is a
    // separately built and separately cached file. So remember what each step
    // actually settled on and use that when looking ahead; the first visit to a
    // step falls back to the current one.
    var stepSizes = Object.create(null);

    function pump() {
      while (active < STAGE_CONCURRENCY && queue.length) {
        var url = queue.shift();
        active++;
        var img = new Image();
        var done = function () { active--; pump(); };
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
        img.decoding = 'async';
        img.srcset = url.srcset;
        img.sizes = url.sizes;
        img.src = url.src;
        // Held by the listeners until it settles.
      }
    }

    function enqueue(raw, sizesHint) {
      var cdnImg = grab('cdnImg'), Q = grab('IMG_Q_PREVIEW');
      var stage = document.getElementById('previewPhoto');
      if (!raw || typeof cdnImg !== 'function' || !stage) return;
      var pv = function (w) { return cdnImg(raw, w, Math.round(w * 5 / 4), { q: Q || 80 }); };
      var src = pv(300);
      if (warmed[raw]) return;
      // The hint of the step this render is FOR, so the browser lands on the
      // rung that step will ask for — not the one this step happens to use.
      // With no hint at all, do not guess: a 100vw guess picks the 900w rung on
      // a phone whose stage paints ~220px, which is a different, colder file
      // than the one the stage will actually request.
      var sizes = sizesHint || stage.getAttribute('sizes');
      if (!sizes) return;
      warmed[raw] = true;
      queue.push({
        src: src,
        srcset: STAGE_LADDER.map(function (w) { return pv(w) + ' ' + w + 'w'; }).join(', '),
        sizes: sizes,
      });
      pump();
    }

    // Nothing to warm for a customizer nobody can see. The step observer fires on
    // the customizer's first render, which happens while it is still collapsed
    // (product page) or held off screen (homepage studio). Warming then looked
    // one step ahead to Skin and pulled all five tone portraits -- with no
    // measured sizes yet, at the 100vw fallback, so 900w each on a phone: ~170 KB
    // on every first visit, including visitors who never open it. The release
    // re-renders, which re-sets data-active-step and schedules this again.
    function studioShowing() {
      if (studio.classList.contains('sbd-customizer--collapsed')) return false;
      return grab('custDeferImages') !== true;
    }

    function run() {
      if (!allowed()) return;
      if (!studioShowing()) return;
      var state = grab('state');
      if (!state) return;

      var stepId = studio.dataset.activeStep;
      var resolve = STAGE_WARM_STEPS[stepId];
      var listFor = STAGE_WARM_LISTS[stepId];

      // The look-ahead runs from EVERY step, not only from a warm-able one.
      // Standing on "face", the next step is hair — which is exactly the case
      // this is for, and an early return here meant it never fired. The current
      // step's own warming is what is conditional, not the look-ahead.
      if (!resolve || !listFor) { warmNextStepStage(state); return; }

      var list = listFor(state, grab);
      if (!Array.isArray(list)) { warmNextStepStage(state); return; }

      var stageEl = document.getElementById('previewPhoto');
      var hint = stageEl && stageEl.getAttribute('sizes');
      if (hint) stepSizes[stepId] = hint;

      var grid = document.querySelector('#questionPanel .opt-grid');
      if (!grid) return;
      var box = grid.getBoundingClientRect();
      var tiles = [].slice.call(grid.querySelectorAll('.opt'));

      // Which tiles are on screen in the shelf right now, plus a margin either
      // side so the next swipe is already covered.
      var first = -1, last = -1;
      tiles.forEach(function (tile, i) {
        var r = tile.getBoundingClientRect();
        if (r.right > box.left && r.left < box.right) {
          if (first < 0) first = i;
          last = i;
        }
      });
      if (first < 0) return;

      for (var i = Math.max(0, first - STAGE_MARGIN); i <= Math.min(tiles.length - 1, last + STAGE_MARGIN); i++) {
        var input = tiles[i].querySelector('input');
        if (!input) continue;
        var option = list.find(function (o) { return o && o.key === input.value; });
        if (option) {
          try { enqueue(resolve(option, state, grab), hint); } catch (error) { /* skip this one */ }
        }
      }

      warmNextStepStage(state);
    }

    /* One step further ahead, so the shopper LANDS on a ready shelf instead of
       waiting the ~2s build out after they arrive.

       Which options will be on screen there is predictable: renderAll() scrolls
       a shelf to centre the current selection, so the arrival window is that
       selection's neighbours. Warming outward from it covers what they see
       first without paying for the whole list.

       Rung: the remembered hint for that step if it has been seen, otherwise
       this step's. A first-visit mismatch costs a wasted warm, never a wrong
       image — the step still requests what it needs. */
    function warmNextStepStage(state) {
      var STEPS = grab('STEPS'), stepIndex = grab('stepIndex'), isVisible = grab('isVisible');
      if (!Array.isArray(STEPS) || typeof stepIndex !== 'number') return;

      for (var i = stepIndex + 1; i < STEPS.length; i++) {
        if (typeof isVisible === 'function' && !isVisible(i)) continue;
        var id = STEPS[i].id;
        var resolve = STAGE_WARM_STEPS[id], listFor = STAGE_WARM_LISTS[id], selFor = STAGE_WARM_SELECTED[id];
        if (!resolve || !listFor || !selFor) return;

        var list = listFor(state, grab);
        if (!Array.isArray(list) || !list.length) return;

        var current = selFor(state);
        var centre = current ? list.findIndex(function (o) { return o && o.key === current.key; }) : 0;
        if (centre < 0) centre = 0;

        var hint = stepSizes[id] || null;
        for (var d = 0; d <= STAGE_LOOKAHEAD; d++) {
          [centre - d, centre + d].forEach(function (at) {
            if (at < 0 || at >= list.length) return;
            try { enqueue(resolve(list[at], state, grab), hint); } catch (error) { /* skip */ }
          });
        }
        return; // one step ahead only
      }
    }

    var timer = 0;
    function schedule() {
      clearTimeout(timer);
      // Settle first: mid-swipe every frame would queue tiles flying past.
      timer = setTimeout(run, 250);
    }

    new MutationObserver(schedule).observe(studio, {
      attributes: true,
      attributeFilter: ['data-active-step'],
    });
    document.addEventListener('scroll', function (event) {
      var t = event.target;
      if (t && t.classList && t.classList.contains('opt-grid')) schedule();
    }, true);
    document.addEventListener('click', function (event) {
      if (event.target.closest && event.target.closest('#questionPanel')) schedule();
    });
    schedule();
  }

  function boot() {
    initShine();
    initLauncher();
    initStudioPlacement();
    initFacesGrid();
    initImmersiveStudio();
    initShelfCounts();
    initStepPrefetch();
    initPreviewSizing();
    initStageWarm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
